import { Link } from "react-router-dom";
import { CheckCircle2, Calendar, Mail, Eye, FileSearch, ClipboardCheck, ShieldCheck, UserCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePageSEO } from "@/hooks/usePageSEO";

const MAILTO = (subject: string) =>
  `mailto:contact@iso27001kit.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
    "Hi,\n\nI'd like to request a consultation. Here are a few details about my organisation and where we are in the ISO 27001 process:\n\n- Organisation:\n- Current stage (planning / pre-Stage 1 / pre-Stage 2 / surveillance):\n- Main concerns:\n\nThanks."
  )}`;

const options = [
  {
    id: "readiness",
    icon: Eye,
    title: "ISO 27001 readiness review",
    forWho: "Teams who have started building an ISMS but are unsure whether they are ready to engage a certification body.",
    reviewed: [
      "ISMS scope and context",
      "Risk methodology and risk register",
      "Statement of Applicability",
      "Core policies and ownership",
    ],
    receives: [
      "Structured readiness feedback",
      "Prioritised list of gaps and quick wins",
      "Suggested next 30–60 day actions",
    ],
    duration: "Suggested duration: ~60 minutes (placeholder)",
    cta: "Request a readiness review",
    subject: "ISO 27001 readiness review request",
  },
  {
    id: "stage1",
    icon: FileSearch,
    title: "Stage 1 document review",
    forWho: "Teams preparing for the Stage 1 (documentation) audit who want a second pair of eyes on their ISMS documents.",
    reviewed: [
      "Mandatory ISO 27001:2022 clauses (4–10)",
      "ISMS policy, scope and objectives",
      "Risk assessment and treatment documentation",
      "Statement of Applicability and justifications",
    ],
    receives: [
      "Document-by-document feedback",
      "Notes on common Stage 1 findings to avoid",
      "Suggested edits and missing items",
    ],
    duration: "Suggested duration: ~90 minutes (placeholder)",
    cta: "Request a Stage 1 review",
    subject: "ISO 27001 Stage 1 document review request",
  },
  {
    id: "stage2",
    icon: ClipboardCheck,
    title: "Stage 2 evidence review",
    forWho: "Teams approaching the Stage 2 (implementation) audit who want to sanity-check evidence before the auditor arrives.",
    reviewed: [
      "Evidence tracker and control coverage",
      "Internal audit programme and results",
      "Management review inputs and outputs",
      "Corrective actions and nonconformities",
    ],
    receives: [
      "Evidence gap list per Annex A theme",
      "Suggested clarifications and reworded records",
      "Realistic view of likely audit questions",
    ],
    duration: "Suggested duration: ~90 minutes (placeholder)",
    cta: "Request a Stage 2 review",
    subject: "ISO 27001 Stage 2 evidence review request",
  },
];

const reviewedItems = [
  "ISMS scope",
  "Risk methodology",
  "Risk register",
  "Statement of Applicability",
  "Core policies",
  "Evidence tracker",
  "Internal audit",
  "Management review",
  "Corrective actions",
];

export default function Consultation() {
  usePageSEO({
    title: "ISO 27001 Consultation | Readiness, Stage 1 & Stage 2 Reviews",
    description: "Request an optional ISO 27001 consultation: readiness review, Stage 1 document review, or Stage 2 evidence review before your certification audit.",
    canonicalPath: "/consultation",
  });

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-background text-foreground py-12 sm:py-16 md:py-20 rounded-2xl sm:rounded-3xl mx-2 sm:mx-4 mt-4 sm:mt-8 border border-border">
        <div className="absolute top-[-150px] left-[-100px] w-[450px] h-[450px] bg-accent/5 rounded-full blur-[110px] pointer-events-none" />
        <div className="absolute bottom-[-100px] right-[-50px] w-[350px] h-[350px] bg-accent/5 rounded-full blur-[100px] pointer-events-none" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6 text-foreground">
            Get a second pair of ISO 27001 eyes before the auditor arrives.
          </h1>
          <p className="text-base sm:text-lg md:text-xl mb-6 sm:mb-8 max-w-3xl mx-auto text-muted-foreground">
            Optional human support for teams who are unsure whether they are ready for a certification audit. We review what you have, point out gaps, and help you focus on what matters before Stage 1 or Stage 2.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
              <a href={MAILTO("ISO 27001 consultation request")}>
                <Calendar className="mr-2 h-5 w-5" />
                Request a consultation
              </a>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-accent/40 text-foreground hover:border-accent hover:text-accent font-semibold">
              <a href="/readiness-check">
                <ShieldCheck className="mr-2 h-5 w-5" />
                Try the readiness check first
              </a>
            </Button>
          </div>
          <p className="mt-6 text-xs text-muted-foreground max-w-2xl mx-auto">
            Consultations are optional support. They do not guarantee a certification pass — only an accredited certification body can certify your ISMS.
          </p>
        </div>
      </section>

      {/* Three options */}
      <section className="py-10 sm:py-16 container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">Three ways we can help</h2>
          <p className="text-muted-foreground">Choose the review that matches where you are in the ISO 27001 journey.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
          {options.map((opt) => {
            const Icon = opt.icon;
            return (
              <Card key={opt.id} className="border shadow-sm hover:shadow-md transition-shadow flex flex-col">
                <CardHeader>
                  <Icon className="h-10 w-10 mb-3 text-primary" />
                  <CardTitle className="text-xl">{opt.title}</CardTitle>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 gap-5">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Who it's for</p>
                    <p className="text-sm text-foreground">{opt.forWho}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">What is reviewed</p>
                    <ul className="space-y-1.5">
                      {opt.reviewed.map((r) => (
                        <li key={r} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">What you receive</p>
                    <ul className="space-y-1.5">
                      {opt.receives.map((r) => (
                        <li key={r} className="flex items-start gap-2 text-sm text-foreground">
                          <CheckCircle2 className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                          <span>{r}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <p className="text-xs text-muted-foreground italic">{opt.duration}</p>
                  <Button asChild className="mt-auto bg-accent hover:bg-accent/90 text-accent-foreground">
                    <a href={MAILTO(opt.subject)}>{opt.cta}</a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Items reviewed */}
      <section className="py-10 sm:py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-foreground mb-3">Examples of items we look at</h2>
          <p className="text-center text-muted-foreground mb-10 max-w-2xl mx-auto">
            Depending on the option you choose, a review can touch any of the following parts of your ISMS.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {reviewedItems.map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3">
                <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expert placeholder */}
      <section className="py-10 sm:py-16 container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3 text-center">Who runs the consultation</h2>
          <p className="text-center text-muted-foreground mb-8">
            Reviews are delivered by practitioners who have implemented and audited ISO 27001 ISMSs. Detailed bios and credentials will be published here once verified.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            <Card className="border-dashed border-2 border-border bg-background">
              <CardHeader>
                <UserCheck className="h-8 w-8 text-primary mb-2" />
                <CardTitle className="text-lg">Lead reviewer — placeholder</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Background, certifications (e.g. lead implementer / lead auditor) and years of ISMS experience will be listed here.
                </p>
              </CardContent>
            </Card>
            <Card className="border-dashed border-2 border-border bg-background">
              <CardHeader>
                <UserCheck className="h-8 w-8 text-primary mb-2" />
                <CardTitle className="text-lg">Supporting reviewer — placeholder</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  Additional reviewer details, focus areas (risk, controls, evidence) and sample engagements will be added once confirmed.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Honest scope */}
      <section className="py-10 sm:py-16 container mx-auto px-4">
        <div className="max-w-4xl mx-auto rounded-2xl border border-border bg-muted/30 p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-3">
            <AlertTriangle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
            <h2 className="text-xl sm:text-2xl font-semibold text-foreground">What a consultation is, and is not</h2>
          </div>
          <ul className="space-y-2 text-sm text-foreground">
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />Optional support to sanity-check your ISMS before engaging a certification body.</li>
            <li className="flex gap-2"><CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />Practical feedback on documents, evidence, and audit-readiness.</li>
            <li className="flex gap-2"><AlertTriangle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />Not a certification audit, and not a guarantee of passing Stage 1 or Stage 2.</li>
            <li className="flex gap-2"><AlertTriangle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />Not a substitute for your internal audit or management review.</li>
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-16 bg-gradient-to-r from-accent/10 to-primary/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">Ready for a second pair of eyes?</h2>
          <p className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl mx-auto">
            Tell us where you are in the process and which review fits best. We'll reply with availability and next steps.
          </p>
          <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold">
            <a href={MAILTO("ISO 27001 consultation request")}>
              <Calendar className="mr-2 h-5 w-5" />
              Request a consultation
            </a>
          </Button>
          <p className="text-sm text-muted-foreground mt-4">
            Not ready for a paid review?{" "}
            <Link to="/iso-27001-implementation-review" className="text-primary underline underline-offset-2 hover:text-primary/80">
              Apply for a complimentary 30-minute implementation review
            </Link>
            . Limited spots available.
          </p>
          <p className="text-sm text-muted-foreground mt-3 flex items-center justify-center gap-2">
            <Mail className="h-4 w-4" />
            Or email{" "}
            <a href="mailto:contact@iso27001kit.com" className="text-primary underline underline-offset-2 hover:text-primary/80">
              contact@iso27001kit.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
